// Auth layer backed by Supabase Auth.
// Keeps the existing localStorage keys (token/username/role/standort/products) in
// sync with the Supabase session so the rest of the app keeps working unchanged,
// while RLS on the database enforces real per-restaurant access from the JWT claims.
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

const KEYS = ['token', 'username', 'role', 'role_raw', 'standort', 'products'];

function mirror(session: Session | null) {
  if (!session) {
    KEYS.forEach((k) => localStorage.removeItem(k));
    return;
  }
  const md: any = session.user.app_metadata || {};
  const rawRole: string = md.role || 'planner';
  // Effective UI role: platform superadmin and client admin both get the admin UI.
  // (The literal claim still drives RLS server-side; this only gates frontend chrome.)
  const uiRole = rawRole === 'admin' || rawRole === 'superadmin' ? 'admin' : rawRole;
  localStorage.setItem('token', session.access_token);
  localStorage.setItem('username', session.user.email || '');
  localStorage.setItem('role', uiRole);
  localStorage.setItem('role_raw', rawRole);
  localStorage.setItem('standort', md.standort || '*');
  localStorage.setItem('products', JSON.stringify(md.products || []));
}

// Mirror on every auth transition: login, token refresh, logout, and the
// INITIAL_SESSION restore that fires on page load.
supabase.auth.onAuthStateChange((_event, session) => mirror(session));

export async function signIn(email: string, password: string): Promise<Session> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  mirror(data.session); // ensure localStorage is populated before we navigate
  return data.session!;
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
  mirror(null);
}
